// @flow
// $FlowFixMe
import React, { useMemo } from "react";
import { get, round, noop } from "lodash";
import Icon from "@fdr/static-ui/Icons";
import { info, arrowRight } from "@fdr/static-ui/Icons/icons";
import buildColor from "@fdr/static-ui/ColorPalette";
import type { BinaryFn } from "@tvg/types/Functional";
import type {
  HelpTypeEnum,
  RunnerInformation,
  SimpleBettingInterest
} from "@fdr/types/ProgramPage";
import { type RaceCardBettingInterest } from "@tvg/types/Race";
import {
  OddsContainer,
  OddsInfo,
  OpeningOdds,
  OddsTrend,
  Odd,
  AnalystsComment,
  TrendsContainer,
  TrendWrapper,
  WinProbOnRace,
  WinProbOddsWrapper,
  WinProbWrapper,
  WinProbRaceOff
} from "./styled-components";

type Props = {
  runner: ?RunnerInformation,
  showBottomBorder: boolean,
  useGraphStatusV2?: boolean,
  numColumns?: number,
  isNumberFireSection: boolean,
  bettingInterest: RaceCardBettingInterest | ?SimpleBettingInterest,
  openHelpModal: BinaryFn<HelpTypeEnum, boolean, void>,
  showRunnerUpdate: boolean,
  isWagerable: boolean,
  showWinProb: boolean
};

const OddsDescription = (props: Props) => {
  const {
    runner,
    showBottomBorder,
    useGraphStatusV2,
    numColumns,
    isNumberFireSection,
    bettingInterest,
    openHelpModal,
    showRunnerUpdate,
    isWagerable,
    showWinProb
  } = props;

  const formatOdd = (numerator: number, denominator: number): string =>
    denominator === 0 ? `${numerator}/1` : `${numerator}/${denominator}`;

  const formatRecentOdd = (odd: string = "") => {
    const isDecimal = /\//g.test(odd);
    if (!isDecimal) {
      return `${odd}/1`;
    }
    return odd;
  };

  const renderTrends = () => {
    const recentOdds = get(runner, "recentOdds", []);
    const newRecentOdds =
      recentOdds && recentOdds.length > 1
        ? recentOdds.slice(1, recentOdds.length)
        : [];
    return newRecentOdds.length > 0 ? (
      newRecentOdds.map((recentOdd, index) => (
        // eslint-disable-next-line react/no-array-index-key
        <TrendWrapper key={`${recentOdd}-${index}`}>
          {index !== newRecentOdds.length - 1 && (
            <Icon
              icon={arrowRight}
              size={12}
              color={buildColor("grey", "900")}
            />
          )}
          <Odd>{formatRecentOdd(recentOdd.odd)}</Odd>
        </TrendWrapper>
      ))
    ) : (
      <Odd>{` N/A`}</Odd>
    );
  };

  const selectionType = numColumns === 1 ? "default" : "exotic";

  // TODO: Remove this logic from this atomic
  const getWinProbability = () => {
    const winProb = get(props, "bettingInterest.runners[0].winProbability", 0);
    return round(winProb * 100);
  };

  // TODO: Remove this logic from atomic
  const calculateWinProb = () => {
    const numerator = +get(props, "bettingInterest.currentOdds.numerator", 99);
    const denominator = +get(
      props,
      "bettingInterest.currentOdds.denominator",
      99
    );

    return round(
      denominator !== 0
        ? (denominator / (numerator + denominator)) * 100
        : (1 / (numerator + 1)) * 100,
      0
    );
  };

  // TODO: Remove this logic from atomic
  const fetchOdds = (): string =>
    get(props, "bettingInterest.odds") !== undefined ? "odds" : "currentOdds";

  const isShowWinProbOnRace = () =>
    selectionType === "default" && !isNumberFireSection && isWagerable;

  const showWinProbOnRace = () => {
    return (
      <React.Fragment>
        {!isNumberFireSection &&
          get(bettingInterest, "currentOdds.numerator", 99) !== 99 && (
            <WinProbOnRace onClick={() => openHelpModal("WIN PROB", false)}>
              WIN PROB:{" "}
              <span>
                {useGraphStatusV2 ? getWinProbability() : calculateWinProb()}
                %
                <Icon color={buildColor("blue", "200")} size={14} icon={info} />
              </span>
            </WinProbOnRace>
          )}
      </React.Fragment>
    );
  };

  const showWinProbRaceOff = () => {
    return (
      <React.Fragment>
        {get(props, `bettingInterest.${fetchOdds()}.numerator`, 99) !== 99 && (
          <WinProbRaceOff onClick={() => openHelpModal("WIN PROB", false)}>
            WIN PROB.{" "}
            <span>
              {" "}
              {calculateWinProb()}%
              <Icon color={buildColor("blue", "200")} size={14} icon={info} />
            </span>
          </WinProbRaceOff>
        )}
      </React.Fragment>
    );
  };

  return useMemo(
    () => (
      <OddsContainer showBorder={showBottomBorder}>
        <OddsInfo>
          <OpeningOdds>
            Opening odds
            <Odd>{` ${formatOdd(
              +get(runner, "morningLineOdds.numerator", 0),
              +get(runner, "morningLineOdds.denominator", 0)
            )}`}</Odd>
          </OpeningOdds>
          <WinProbOddsWrapper>
            {showRunnerUpdate && showWinProb ? (
              <WinProbWrapper>
                {isShowWinProbOnRace()
                  ? showWinProbOnRace()
                  : showWinProbRaceOff()}
              </WinProbWrapper>
            ) : (
              ""
            )}
            <OddsTrend>
              ODDS TREND
              <TrendsContainer>{renderTrends()}</TrendsContainer>
            </OddsTrend>
          </WinProbOddsWrapper>
        </OddsInfo>
        {!!get(runner, "runners[0].timeform.analystsComments") && (
          <AnalystsComment>
            {get(runner, "runners[0].timeform.analystsComments")}
          </AnalystsComment>
        )}
      </OddsContainer>
    ),
    [
      showBottomBorder,
      JSON.stringify(get(runner, "recentOdds")),
      showWinProb,
      numColumns
    ]
  );
};

OddsDescription.defaultProps = {
  runner: null,
  showBottomBorder: true,
  useGraphStatusV2: false,
  numColumns: 1,
  isNumberFireSection: false,
  bettingInterest: noop,
  openHelpModal: noop,
  showRunnerUpdate: false,
  isWagerable: true,
  showWinProb: true
};

export default OddsDescription;
